import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { validate as uuidValidate } from 'uuid';
import { connect } from 'react-redux';
import {
  Button,
  Form,
  FormGroup,
  Input,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { APIgetFile } from '../api/Files';
import { getFileExtension as getFileExtensionHelper } from '../helpers/Files';

import LogoUploader from './Admin/LogoUploader';
import ErrorBoundary from './ErrorBoundary';
import ProcessingSpinner from './ProcessingSpinner';

const normalizeAllowedExtensions = (extensions = []) => (
  (Array.isArray(extensions) ? extensions : [])
    .map(ext => String(ext || '').trim().toLowerCase().replace(/^\./, ''))
    .filter(Boolean)
);

const getFileExtension = (fileData, filePath) => {
  const path = fileData?.path || filePath || '';
  const rawExtension = getFileExtensionHelper({
    ...(fileData || {}),
    path,
  });
  const normalizedExtension = String(rawExtension || '').trim().toLowerCase().replace(/^\./, '');

  if (!normalizedExtension) {
    return '';
  }

  if (!fileData?.extension && String(path).indexOf('.') === -1) {
    return '';
  }

  return normalizedExtension;
};

const getFileUuidFromPath = (path = '') => {
  const normalizedPath = String(path || '').split('?')[0].split('#')[0];
  const fileName = normalizedPath.split('/').pop() || '';
  const dotIndex = fileName.lastIndexOf('.');
  const baseName = dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;

  return uuidValidate(baseName) ? baseName : '';
};

const buildFallbackFileData = (path, url, originalFileName = '') => {
  const fileName = path ? String(path).split('/').pop() : '';
  const normalizedPath = String(path || '').split('?')[0].split('#')[0];
  const inferredExtension = String(getFileExtensionHelper({ path: normalizedPath }) || '').toLowerCase();
  const extension = normalizedPath.includes('.') ? inferredExtension : '';
  const originalName = String(originalFileName || '').trim() || fileName;

  return {
    uuid: getFileUuidFromPath(path),
    path: path || '',
    name: originalName,
    display_name: originalName,
    original_name: originalName,
    uploaded_via_logo_uploader: true,
    extension,
    url: url || '',
    urls: {
      preview: url || '',
    },
  };
};

const FileSelectModalEnhanced = ({
  handleSelectFile,
  handleFileUrl,
  handleClose,
  allowUpload,
  showRemoveButton,
  showThumbnail,
  removeButtonText,
  handleRemove,
  handleFileData,
  allowedFileExtensions,
}) => {
  const intl = useIntl();
  const [fileUuid, setFileUuid] = useState('');
  const [filePath, setFilePath] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [uploadedOriginalName, setUploadedOriginalName] = useState('');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const normalizedAllowedExtensions = normalizeAllowedExtensions(allowedFileExtensions);
  const hasExtensionRestriction = normalizedAllowedExtensions.length > 0;
  const uploadAccept = hasExtensionRestriction
    ? normalizedAllowedExtensions.map(ext => `.${ext}`).join(', ')
    : '.png, .jpg, .jpeg';
  const selectedFileExtension = getFileExtension(fileData, filePath);
  const isSelectedFileAllowed = !hasExtensionRestriction
    || !filePath
    || (selectedFileExtension && normalizedAllowedExtensions.includes(selectedFileExtension));

  const loadFile = () => {
    if (!uuidValidate(fileUuid)) {
      setErrorMessage(intl.formatMessage({ defaultMessage: 'This UUID does not look valid' }));
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);

    APIgetFile(fileUuid).then((response) => {
      const { data } = response;
      if (data) {
        const selectedUrl = hasExtensionRestriction
          ? (data.urls?.original || data.url || data.urls?.preview || '')
          : (data.urls?.preview || data.url || data.urls?.original || '');

        setFilePath(data.path);
        setFileUrl(selectedUrl);
        setFileData(data);
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
      setErrorMessage(intl.formatMessage({ defaultMessage: 'File with this UUID does not exist' }));
      setError(true);
    });
  };

  const handleChange = (e) => {
    const { value } = e.target;
    setFileUuid(value);
  };

  const handleEnter = (e) => {
    if (e.key === 'Enter') {
      loadFile();
    }
  };

  const handleConfirm = () => {
    const normalizedFileData = fileData || buildFallbackFileData(
      filePath,
      fileUrl,
      uploadedOriginalName,
    );

    handleSelectFile(filePath);
    handleFileUrl(fileUrl);
    handleFileData(normalizedFileData);
    handleClose();
  };

  const isPDF = () => {
    if (!fileData) return false;

    return getFileExtension(fileData) === 'pdf';
  };

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose}>
        {intl.formatMessage({ defaultMessage: 'Select file' })}
      </ModalHeader>
      <ModalBody>
        <Form className="file-select-form" onSubmit={(e) => { e.preventDefault(); }}>
          <div className="file-select-form-inputs">
            <FormGroup>
              <Input
                type="text"
                id="fileUuid"
                name="fileUuid"
                className="file-uuid"
                placeholder={intl.formatMessage({ defaultMessage: 'Enter file ID' })}
                disabled={loading}
                value={fileUuid}
                autoFocus={true}
                invalid={error}
                onChange={(e) => handleChange(e)}
                onKeyUp={(e) => handleEnter(e)}
              />
              <Button
                disabled={!fileUuid || loading}
                onClick={() => loadFile()}
              >
                {loading ? (
                  <i className="far fa-spinner fa-spin" />
                ) : (
                  <i className="far fa-search" />
                )}
              </Button>
            </FormGroup>
            {allowUpload && (
              <>
                <span>{intl.formatMessage({ defaultMessage: 'or' })}</span>
                <LogoUploader
                  name="share-logo-uploader"
                  uploadButtonText="Upload"
                  uploadButtonIcon="fa-upload"
                  accept={uploadAccept}
                  previewOriginal={hasExtensionRestriction}
                  handleChangeImage={(url, originalFileName) => {
                    setFilePath(url);
                    setUploadedOriginalName(String(originalFileName || '').trim());
                    setFileData(buildFallbackFileData(url, fileUrl, originalFileName));
                  }}
                  handleChangePreview={(url) => {
                    setFileUrl(url);
                    setFileData(prev => buildFallbackFileData(
                      prev?.path || filePath,
                      url,
                      prev?.original_name || uploadedOriginalName,
                    ));
                  }}
                />
              </>
            )}
          </div>
          {error && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {errorMessage}
            </div>
          )}
          {hasExtensionRestriction && (
            <div className="text-muted mt-2">
              {intl.formatMessage({ defaultMessage: 'Allowed file extensions are:' })} {normalizedAllowedExtensions.map(ext => `.${ext}`).join(', ')}
            </div>
          )}
        </Form>
        {filePath && (
          <div className="file-selected">
            <h5>{intl.formatMessage({ defaultMessage: 'Selected file' })}</h5>
            {showThumbnail && (
              <div className="image-container">
                <div className="image-wrapper">
                  {isPDF() ? (
                    <img src={fileData.urls.thumbnail} alt="Selected file" />
                  ) : (
                    <img src={fileUrl} alt="Selected file" />
                  )}
                </div>
              </div>
            )}
            {fileData?.display_name && (
              <p>
                {fileData.display_name}
              </p>
            )}
            {hasExtensionRestriction && !isSelectedFileAllowed && (
              <div className="invalid-feedback" style={{ display: 'block' }}>
                {selectedFileExtension
                  ? intl.formatMessage(
                    { defaultMessage: 'Selected file extension .{extension} is not allowed.' },
                    { extension: selectedFileExtension },
                  )
                  : intl.formatMessage({ defaultMessage: 'Selected file has no detectable extension and cannot be used in this picker.' })}
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-sb">
        <div className="buttons-group">
          {showRemoveButton && (
            <Button
              color="secondary"
              disabled={removeLoading}
              onClick={() => {
                setRemoveLoading(true);
                handleRemove().then(() => {
                  setRemoveLoading(false);
                  handleClose();
                });
              }}
            >
              <ProcessingSpinner show={removeLoading} />
              {removeButtonText}
            </Button>
          )}
        </div>
        <div className="buttons-group">
          <Button
            color="secondary"
            disabled={loading}
            onClick={() => handleClose()}
          >
            {intl.formatMessage({ defaultMessage: 'Cancel' })}
          </Button>
          <Button
            className="confirmation-button"
            color="primary"
            disabled={loading || !filePath || !isSelectedFileAllowed}
            onClick={() => {
              handleConfirm();
            }}
          >
            {intl.formatMessage({ defaultMessage: 'Confirm' })}
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  client: state.Auth.profile?.client,
});

FileSelectModalEnhanced.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleSelectFile: PropTypes.func.isRequired,
  handleFileUrl: PropTypes.func,
  allowUpload: PropTypes.bool,
  showRemoveButton: PropTypes.bool,
  showThumbnail: PropTypes.bool,
  removeButtonText: PropTypes.string,
  handleRemove: PropTypes.func,
  handleFileData: PropTypes.func,
  allowedFileExtensions: PropTypes.arrayOf(PropTypes.string),
};

FileSelectModalEnhanced.defaultProps = {
  allowUpload: true,
  showRemoveButton: false,
  showThumbnail: true,
  removeButtonText: 'Remove',
  handleFileUrl: () => {},
  handleFileData: () => {},
  handleRemove: () => {},
  allowedFileExtensions: [],
};

export default connect(mapStateToProps)(FileSelectModalEnhanced);
